import React from "react";
import { BarCustomLayerProps, BarDatum } from "@nivo/bar";

interface CreateProjectionBarLabelsLayerOptions {
  /** Formats the revenue value (receives the bar's raw value, in millions). */
  formatRevenue: (value: number) => string;
  /** Period-over-period growth (decimal) per datum index; null hides the label. */
  growthByIndex: (number | null)[];
  revenueColor: string;
  growthPositiveColor: string;
  growthNegativeColor: string;
  isModal?: boolean;
}

const FONT_FAMILY = "system-ui, -apple-system, sans-serif";

/**
 * Custom label layer for the revenue projections chart:
 * - the revenue value is drawn just BELOW each bar (kept off the dashed fill so it
 *   stays legible),
 * - the period-over-period growth is drawn ABOVE each bar (green up / red down),
 * so the per-period growth reads alongside the CAGR trend line.
 */
export const createProjectionBarLabelsLayer = ({
  formatRevenue,
  growthByIndex,
  revenueColor,
  growthPositiveColor,
  growthNegativeColor,
  isModal = false,
}: CreateProjectionBarLabelsLayerOptions) => {
  const fontSize = isModal ? 11.5 : 10.5;
  const minBarWidth = isModal ? 36 : 30;

  const ProjectionBarLabelsLayer: React.FC<BarCustomLayerProps<BarDatum>> = ({ bars }) => (
    <g>
      {bars.map((bar) => {
        if (bar.width < minBarWidth) return null;

        const value = bar.data.value ?? 0;
        const centerX = bar.x + bar.width / 2;

        const growth = growthByIndex[bar.data.index];
        const showGrowth = growth != null && Number.isFinite(growth);

        return (
          <g key={bar.key}>
            {showGrowth && (
              <text
                x={centerX}
                y={bar.y - (isModal ? 8 : 6)}
                textAnchor="middle"
                dominantBaseline="auto"
                style={{
                  fontSize,
                  fontWeight: 700,
                  fill: growth >= 0 ? growthPositiveColor : growthNegativeColor,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {`${growth >= 0 ? "+" : ""}${(growth * 100).toFixed(1)}%`}
              </text>
            )}

            <text
              x={centerX}
              y={bar.y + bar.height + (isModal ? 8 : 6)}
              textAnchor="middle"
              dominantBaseline="hanging"
              style={{
                fontSize,
                fontWeight: 700,
                fill: revenueColor,
                fontFamily: FONT_FAMILY,
              }}
            >
              {formatRevenue(value)}
            </text>
          </g>
        );
      })}
    </g>
  );

  return ProjectionBarLabelsLayer;
};
